import type { Catch } from "@kalaporssi/engine";
import type { PublicPlayer, RoundResultResponse } from "@kalaporssi/shared";
import { species, speciesById } from "@kalaporssi/shared";
import { feur, fkg, fp, monthName, pct } from "../lib/format";
import { CatchCard } from "./CatchCard";

/** Exchange board: forecast, actual demand, supply, feed, price and its gap to the forecast. */
export function MarketTable({ result }: { result: RoundResultResponse }) {
  return (
    <div className="panel">
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Laji</th>
              <th>Ennuste</th>
              <th>Toteutunut kysyntä</th>
              <th>Tarjonta</th>
              <th>Rehuksi</th>
              <th>Hintaennuste</th>
              <th>Hinta €/kg</th>
              <th>Ero</th>
            </tr>
          </thead>
          <tbody>
            {species.map((s) => {
              const m = result.market[s.id];
              if (!m) return null;
              const d = m.price == null ? null : m.price / m.naivePrice - 1;
              return (
                <tr key={s.id}>
                  <td>
                    <span className="chip" style={{ background: s.color }} />
                    {s.name}
                  </td>
                  <td className="num">{fkg(m.forecastDemand)}</td>
                  <td className="num">{fkg(m.demand)}</td>
                  <td className="num">
                    <b>{fkg(m.supply)}</b>
                  </td>
                  <td className="num">{m.feed > 0 ? <span className="down">{fkg(m.feed)}</span> : "–"}</td>
                  <td className="num">{fp(m.naivePrice)}</td>
                  <td className="num">
                    <b>{fp(m.price)}</b>
                  </td>
                  <td className="num">{d == null ? "–" : <span className={d >= 0 ? "up" : "down"}>{pct(d)}</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Month results in rank order; the viewer's own card is highlighted. */
export function MonthResults(props: {
  result: RoundResultResponse;
  catches: Catch[];
  players: PublicPlayer[];
  meId?: string;
}) {
  const { result, catches } = props;
  const byId = new Map(props.players.map((p) => [p.id, p]));
  const ranked = Object.keys(result.revenue).sort((a, b) => (result.revenue[b] ?? 0) - (result.revenue[a] ?? 0));
  return (
    <div className="results">
      {ranked.map((pid, k) => {
        const c = catches[result.choices[pid] ?? -1];
        const feed = result.feedKg[pid] ?? 0;
        return (
          <div key={pid} className={`panel res${pid === props.meId ? " me" : ""}`}>
            <div className="row spread">
              <b>
                {k + 1}. {byId.get(pid)?.nickname ?? pid}
                {pid === props.meId && " (sinä)"}
              </b>
              <span className="muted">#{c?.number}</span>
            </div>
            <div className="eur num">{feur(result.revenue[pid] ?? 0)}</div>
            {c && (
              <div className="muted small">
                {c.place}: {c.items.map((it) => `${speciesById[it.speciesId]?.name} ${it.kg} kg`).join(", ")}
                {feed >= 1 && (
                  <>
                    <br />
                    Rehuksi {fkg(feed)}
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Catch grid tagged with who picked each one. */
export function WhoChose(props: { result: RoundResultResponse; catches: Catch[]; players: PublicPlayer[] }) {
  const byId = new Map(props.players.map((p) => [p.id, p]));
  return (
    <div className="grid">
      {props.catches.map((c) => {
        const who = Object.entries(props.result.choices)
          .filter(([, idx]) => idx === c.index)
          .map(([pid]) => byId.get(pid)?.nickname ?? pid);
        return (
          <CatchCard
            key={c.index}
            c={c}
            footer={
              who.length > 0 && (
                <div className="who">
                  {who.map((n) => (
                    <span key={n}>{n}</span>
                  ))}
                </div>
              )
            }
          />
        );
      })}
    </div>
  );
}

/** Standings as horizontal bars. Removed players stay listed with a note (SPEC §10.4). */
export function Standings({ players, meId }: { players: PublicPlayer[]; meId?: string }) {
  const ranked = [...players].sort((a, b) => b.total - a.total);
  const max = Math.max(1, ...ranked.map((p) => p.total));
  return (
    <div className="panel bars">
      {ranked.map((p) => (
        <div key={p.id} className={`barrow${p.id === meId ? " me" : ""}`}>
          <span>
            {p.nickname}
            {!p.active && <span className="muted small"> (poistui)</span>}
          </span>
          <div className="track">
            <div className="fill" style={{ width: `${Math.max(2, (p.total / max) * 100)}%` }} />
          </div>
          <span className="num">{feur(p.total)}</span>
        </div>
      ))}
    </div>
  );
}

/** Month-by-month revenue table for the final screen. */
export function MonthTable(props: { results: (RoundResultResponse | undefined)[]; players: PublicPlayer[] }) {
  const ranked = [...props.players].sort((a, b) => b.total - a.total);
  return (
    <div className="panel">
      <h3>Kuukausitulot (€)</h3>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Pelaaja</th>
              {props.results.map((_r, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: one column per month
                <th key={i}>{monthName(i).slice(0, 3)}</th>
              ))}
              <th>Yht.</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((p) => (
              <tr key={p.id}>
                <td>{p.nickname}</td>
                {props.results.map((r, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: one column per month
                  <td key={i} className="num">
                    {r?.revenue[p.id] == null ? "–" : Math.round(r.revenue[p.id] ?? 0)}
                  </td>
                ))}
                <td className="num">
                  <b>{Math.round(p.total)}</b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
